import { useCallback, useEffect, useMemo, useState } from 'react'
import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { toPosTotals } from '@/components/pos/posCart'
import { fetchPosDiscountReasons } from '@/lib/api/pos'
import { cn } from '@/lib/utils'

const QUICK_PERCENT = [10, 20]
const QUICK_FLAT = [50]

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {'line' | 'bill' | 'voucher'} props.initialTab
 * @param {import('@/types/api').Sale | null} props.sale
 * @param {number | null} [props.lineId]
 * @param {boolean} props.submitting
 * @param {string} [props.apiError]
 * @param {(payload: object) => Promise<void>} [props.onApplyLineDiscount]
 * @param {(payload: object) => Promise<void>} [props.onApplyHeaderDiscount]
 * @param {(payload: object) => Promise<void>} [props.onApplyPromoCode]
 * @param {() => Promise<void>} [props.onRemovePromoCode]
 */
export function PosDiscountModal({
  open,
  onClose,
  initialTab,
  sale,
  lineId = null,
  submitting,
  apiError = '',
  onApplyLineDiscount,
  onApplyHeaderDiscount,
  onApplyPromoCode,
  onRemovePromoCode,
}) {
  const [tab, setTab] = useState(initialTab)
  const [reasons, setReasons] = useState([])
  const [loadingReasons, setLoadingReasons] = useState(false)
  const [discountType, setDiscountType] = useState('percent')
  const [value, setValue] = useState('')
  const [reasonId, setReasonId] = useState('')
  const [promoCode, setPromoCode] = useState('')
  const [managerEmail, setManagerEmail] = useState('')
  const [managerPassword, setManagerPassword] = useState('')
  const [localError, setLocalError] = useState('')
  const [needsManager, setNeedsManager] = useState(false)

  const totals = useMemo(() => toPosTotals(sale), [sale])

  useEffect(() => {
    if (!open) return
    setTab(initialTab)
    setValue('')
    setPromoCode('')
    setManagerEmail('')
    setManagerPassword('')
    setLocalError('')
    setNeedsManager(false)
  }, [initialTab, open])

  useEffect(() => {
    if (!open) return undefined

    let active = true
    setLoadingReasons(true)

    fetchPosDiscountReasons()
      .then((items) => {
        if (!active) return
        setReasons(items)
        if (items[0]) {
          setReasonId(String(items[0].id))
        }
      })
      .catch(() => {
        if (active) setReasons([])
      })
      .finally(() => {
        if (active) setLoadingReasons(false)
      })

    return () => {
      active = false
    }
  }, [open])

  const applyDiscount = useCallback(async () => {
    setLocalError('')
    setNeedsManager(false)

    const payload = {
      type: discountType,
      value: Number(value),
      discount_reason_id: Number(reasonId),
    }

    if (needsManager) {
      payload.manager_email = managerEmail
      payload.manager_password = managerPassword
    }

    try {
      if (tab === 'line') {
        if (!lineId) {
          setLocalError('Select a ticket line first.')
          return
        }
        await onApplyLineDiscount?.({ lineId, ...payload })
      } else if (tab === 'bill') {
        await onApplyHeaderDiscount?.(payload)
      } else {
        await onApplyPromoCode?.({
          code: promoCode.trim(),
          manager_email: managerEmail || undefined,
          manager_password: managerPassword || undefined,
        })
      }
      onClose()
    } catch (err) {
      const errors = err?.response?.data?.errors
      if (errors?.manager_email || errors?.manager_password) {
        setNeedsManager(true)
      }
      setLocalError(err?.response?.data?.message ?? 'Unable to apply discount.')
    }
  }, [
    discountType,
    lineId,
    managerEmail,
    managerPassword,
    needsManager,
    onApplyHeaderDiscount,
    onApplyLineDiscount,
    onApplyPromoCode,
    onClose,
    promoCode,
    reasonId,
    tab,
    value,
  ])

  if (!open) return null

  const displayError = localError || apiError

  return (
    <div className="pos-v2-modal-backdrop" role="presentation">
      <div className="pos-v2-modal" role="dialog" aria-label="Discount and vouchers">
        <div className="flex items-center justify-between border-b border-[#E5E7EB] px-4 py-3">
          <h2 className="text-sm font-bold text-slate-900">Discount & Voucher</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"
            aria-label="Close"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="flex border-b border-[#E5E7EB]">
          {[
            { id: 'line', label: 'Line' },
            { id: 'bill', label: 'Bill' },
            { id: 'voucher', label: 'Voucher' },
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(/** @type {'line' | 'bill' | 'voucher'} */ (item.id))}
              className={cn(
                'min-h-[44px] flex-1 text-[11px] font-semibold',
                tab === item.id
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-slate-500',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>

        <div className="space-y-3 p-4">
          {Number(totals.discount) > 0 || sale?.promo_code_id ? (
            <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-900">
              <p>
                Applied discount: AED {totals.discount.toFixed(2)}
                {sale?.discount_reason_id ? ` · Reason #${sale.discount_reason_id}` : ''}
                {sale?.promo_code_id ? ` · Promo #${sale.promo_code_id}` : ''}
              </p>
              {sale?.promo_code_id && onRemovePromoCode ? (
                <button
                  type="button"
                  className="mt-1 font-semibold text-emerald-800 underline"
                  disabled={submitting}
                  onClick={() => onRemovePromoCode()}
                >
                  Remove voucher
                </button>
              ) : null}
            </div>
          ) : null}

          {tab === 'voucher' ? (
            <label className="block space-y-1">
              <span className="text-[10px] font-bold uppercase text-slate-500">Promo code</span>
              <input
                value={promoCode}
                onChange={(event) => setPromoCode(event.target.value)}
                className="pos-v2-drawer-input"
                placeholder="Enter voucher code"
                disabled={submitting}
              />
            </label>
          ) : (
            <>
              <div className="flex flex-wrap gap-1">
                {QUICK_PERCENT.map((pct) => (
                  <button
                    key={pct}
                    type="button"
                    disabled={submitting}
                    onClick={() => {
                      setDiscountType('percent')
                      setValue(String(pct))
                    }}
                    className="min-h-[36px] rounded-md border border-[#E5E7EB] px-3 text-[11px] font-semibold hover:border-primary hover:text-primary"
                  >
                    {pct}%
                  </button>
                ))}
                {QUICK_FLAT.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    disabled={submitting}
                    onClick={() => {
                      setDiscountType('flat')
                      setValue(String(amount))
                    }}
                    className="min-h-[36px] rounded-md border border-[#E5E7EB] px-3 text-[11px] font-semibold hover:border-primary hover:text-primary"
                  >
                    AED {amount}
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <label className="block space-y-1">
                  <span className="text-[10px] font-bold uppercase text-slate-500">Type</span>
                  <select
                    value={discountType}
                    onChange={(event) => setDiscountType(event.target.value)}
                    className="pos-v2-drawer-input"
                    disabled={submitting}
                  >
                    <option value="percent">Percent</option>
                    <option value="flat">Fixed AED</option>
                  </select>
                </label>
                <label className="block space-y-1">
                  <span className="text-[10px] font-bold uppercase text-slate-500">Value</span>
                  <input
                    type="number"
                    min="0"
                    value={value}
                    onChange={(event) => setValue(event.target.value)}
                    className="pos-v2-drawer-input"
                    disabled={submitting}
                  />
                </label>
              </div>

              <label className="block space-y-1">
                <span className="text-[10px] font-bold uppercase text-slate-500">Reason</span>
                <select
                  value={reasonId}
                  onChange={(event) => setReasonId(event.target.value)}
                  className="pos-v2-drawer-input"
                  disabled={submitting || loadingReasons}
                >
                  {reasons.map((reason) => (
                    <option key={reason.id} value={reason.id}>
                      {reason.name}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}

          {(needsManager || tab === 'voucher') ? (
            <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="text-[10px] font-semibold text-amber-900">Manager approval required</p>
              <input
                type="email"
                value={managerEmail}
                onChange={(event) => setManagerEmail(event.target.value)}
                className="pos-v2-drawer-input"
                placeholder="Manager email"
                disabled={submitting}
              />
              <input
                type="password"
                value={managerPassword}
                onChange={(event) => setManagerPassword(event.target.value)}
                className="pos-v2-drawer-input"
                placeholder="Manager password"
                disabled={submitting}
              />
            </div>
          ) : null}

          {displayError ? <p className="text-[11px] text-destructive">{displayError}</p> : null}
        </div>

        <div className="flex gap-2 border-t border-[#E5E7EB] p-4">
          <Button type="button" variant="outline" className="min-h-[44px]" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" className="min-h-[44px] flex-1" disabled={submitting} onClick={applyDiscount}>
            {submitting ? 'Applying…' : 'Apply'}
          </Button>
        </div>
      </div>
    </div>
  )
}
